import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../services/api';
import { toast } from 'react-toastify';
import { LuPlus, LuTrash2, LuDownload, LuPencil, LuFileText, LuCalendar, LuRefreshCw } from 'react-icons/lu';

const CONTRACT_TYPES = [
  { value: 'service_agreement', label: 'Service Agreement' },
  { value: 'storage', label: 'Storage Agreement' },
  { value: 'volume_discount', label: 'Volume Discount' },
  { value: 'nda', label: 'NDA' },
  { value: 'other', label: 'Other' }
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800' },
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'expired', label: 'Expired', color: 'bg-red-100 text-red-800' },
  { value: 'terminated', label: 'Terminated', color: 'bg-gray-100 text-gray-800' }
];

const getStatusBadge = (status) => {
  const opt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[3];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${opt.color}`}>{opt.label}</span>;
};

/**
 * Contracts management tab for admin customer detail.
 * Props:
 *   companyId - UUID of the company
 *   readOnly - if true, hides create/edit/delete actions (for customer portal)
 */
const ContractsTab = ({ companyId, readOnly = false }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [formData, setFormData] = useState({
    title: '', contract_type: 'service_agreement', status: 'active',
    start_date: '', end_date: '', renewal_date: '', auto_renew: false,
    terms: '', notes: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const { data, isLoading } = useQuery(
    ['contracts', companyId],
    () => adminAPI.getContracts({ company_id: companyId }),
    { enabled: !!companyId }
  );

  const contracts = data?.data || [];

  const createMutation = useMutation(
    (fd) => editingContract ? adminAPI.updateContract(editingContract.id, fd) : adminAPI.createContract(fd),
    {
      onSuccess: () => {
        toast.success(editingContract ? 'Contract updated' : 'Contract created');
        queryClient.invalidateQueries(['contracts', companyId]);
        resetForm();
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to save contract')
    }
  );

  const deleteMutation = useMutation(
    (id) => adminAPI.deleteContract(id),
    {
      onSuccess: () => {
        toast.success('Contract deleted');
        queryClient.invalidateQueries(['contracts', companyId]);
      },
      onError: () => toast.error('Failed to delete contract')
    }
  );

  const resetForm = () => {
    setShowForm(false);
    setEditingContract(null);
    setSelectedFile(null);
    setFormData({ title: '', contract_type: 'service_agreement', status: 'active', start_date: '', end_date: '', renewal_date: '', auto_renew: false, terms: '', notes: '' });
  };

  const handleEdit = (contract) => {
    setEditingContract(contract);
    setFormData({
      title: contract.title || '',
      contract_type: contract.contract_type || 'service_agreement',
      status: contract.status || 'active',
      start_date: contract.start_date || '',
      end_date: contract.end_date || '',
      renewal_date: contract.renewal_date || '',
      auto_renew: contract.auto_renew || false,
      terms: contract.terms || '',
      notes: contract.notes || ''
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('company_id', companyId);
    Object.entries(formData).forEach(([key, val]) => {
      if (val !== '' && val !== null) fd.append(key, val);
    });
    if (selectedFile) fd.append('file', selectedFile);
    createMutation.mutate(fd);
  };

  if (isLoading) {
    return <div className="py-8 text-center text-gray-500">Loading contracts...</div>;
  }

  return (
    <div>
      {!readOnly && (
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-medium text-gray-900">Contracts</h4>
          <button type="button" onClick={() => { resetForm(); setShowForm(true); }}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700">
            <LuPlus className="h-4 w-4 mr-1" /> Add Contract
          </button>
        </div>
      )}

      {/* Contract Form */}
      {showForm && (
        <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            {editingContract ? 'Edit Contract' : 'New Contract'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
                <input type="text" required value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <select value={formData.contract_type} onChange={(e) => setFormData(p => ({ ...p, contract_type: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  {CONTRACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select value={formData.status} onChange={(e) => setFormData(p => ({ ...p, status: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                <input type="date" value={formData.start_date} onChange={(e) => setFormData(p => ({ ...p, start_date: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                <input type="date" value={formData.end_date} onChange={(e) => setFormData(p => ({ ...p, end_date: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Renewal Date</label>
                <input type="date" value={formData.renewal_date} onChange={(e) => setFormData(p => ({ ...p, renewal_date: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
              </div>
            </div>
            <label className="flex items-center text-sm">
              <input type="checkbox" checked={formData.auto_renew} onChange={(e) => setFormData(p => ({ ...p, auto_renew: e.target.checked }))} className="mr-2 rounded" />
              Auto-renew
            </label>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Terms / Key Points</label>
              <textarea rows="2" value={formData.terms} onChange={(e) => setFormData(p => ({ ...p, terms: e.target.value }))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Internal Notes</label>
              <textarea rows="2" value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Contract Document (PDF)</label>
              <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setSelectedFile(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" />
              {editingContract?.file_name && !selectedFile && (
                <p className="text-xs text-gray-500 mt-1">Current: {editingContract.file_name}</p>
              )}
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button type="button" onClick={resetForm} className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={createMutation.isLoading}
                className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">
                {createMutation.isLoading ? 'Saving...' : (editingContract ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Contracts List */}
      {contracts.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm">
          {readOnly ? 'No contracts available.' : 'No contracts yet. Click "Add Contract" to create one.'}
        </div>
      ) : (
        <div className="space-y-3">
          {contracts.map(contract => (
            <div key={contract.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <LuFileText className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{contract.title}</span>
                    {getStatusBadge(contract.status)}
                  </div>
                  <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                    <span>{CONTRACT_TYPES.find(t => t.value === contract.contract_type)?.label || contract.contract_type}</span>
                    {contract.start_date && (
                      <span className="flex items-center">
                        <LuCalendar className="h-3 w-3 mr-1" />
                        {contract.start_date}{contract.end_date ? ` to ${contract.end_date}` : ''}
                      </span>
                    )}
                    {contract.auto_renew && (
                      <span className="flex items-center text-blue-600">
                        <LuRefreshCw className="h-3 w-3 mr-1" /> Auto-renew
                      </span>
                    )}
                  </div>
                  {contract.terms && (
                    <p className="mt-2 text-xs text-gray-600">{contract.terms}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  {(contract.file_url || contract.signedFileUrl) && (
                    <a href={contract.signedFileUrl || contract.file_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center px-2 py-1 text-xs text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100">
                      <LuDownload className="h-3 w-3 mr-1" /> {contract.file_name || 'Download'}
                    </a>
                  )}
                  {!readOnly && (
                    <>
                      <button type="button" onClick={() => handleEdit(contract)} className="text-gray-400 hover:text-gray-600">
                        <LuPencil className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => {
                        if (window.confirm('Delete this contract?')) deleteMutation.mutate(contract.id);
                      }} className="text-gray-400 hover:text-red-600">
                        <LuTrash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractsTab;
